import { useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { Loader2 } from "lucide-react";
import { SidebarTrigger } from "./ui/sidebar";
import { useHeaderIpc } from "./ipc/use-header-ipc";
import { EnvironmentSelector } from "./connection/EnvironmentSelector";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { ServerWithName } from "@/hooks/use-app-state";
import { getConnectionStatusMeta } from "./connection/server-card-utils";
import { Button } from "./ui/button";

interface HeaderProps {
  connectedServerConfigs?: Record<string, ServerWithName>;
  onReconnect?: (serverName: string) => Promise<{ success: true } | undefined>;
}

export const Header = ({
  connectedServerConfigs,
  onReconnect,
}: HeaderProps) => {
  const { activeIpc, dismissActiveIpc } = useHeaderIpc();
  const { activeWorkspace } = useAppWorkspace();
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Query servers to find the primary server name
  const workspaceServers = useQuery(
    api.appWorkspaceServers.listServers,
    activeWorkspace?._id ? { workspaceId: activeWorkspace._id } : "skip",
  );

  // Get the primary server's connection status
  const primaryServerStatus = useMemo(() => {
    if (
      !activeWorkspace?.primaryServerId ||
      !workspaceServers ||
      !connectedServerConfigs
    ) {
      return null;
    }

    // Find the primary server record to get its name
    const primaryServerRecord = workspaceServers.find(
      (s) => s._id === activeWorkspace.primaryServerId,
    );

    if (!primaryServerRecord) return null;

    // Look up the server in connectedServerConfigs by name
    const serverConfig = connectedServerConfigs[primaryServerRecord.name];
    if (!serverConfig) return null;

    return {
      name: primaryServerRecord.name,
      status: serverConfig.connectionStatus,
    };
  }, [
    activeWorkspace?.primaryServerId,
    workspaceServers,
    connectedServerConfigs,
  ]);

  // Get status display info
  const statusMeta = primaryServerStatus
    ? getConnectionStatusMeta(primaryServerStatus.status)
    : null;

  const isConnected = primaryServerStatus?.status === "connected";

  const handleReconnect = async () => {
    if (!primaryServerStatus?.name || !onReconnect) return;

    setIsReconnecting(true);
    try {
      await onReconnect(primaryServerStatus.name);
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleStatusClick = () => {
    window.location.hash = "settings/mcp-server";
  };

  return (
    <header className="flex shrink-0 flex-col border-b transition-[width,height] ease-linear">
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 px-4 lg:px-6 drag">
        <div className="flex items-center gap-1 lg:gap-2 no-drag">
          <SidebarTrigger className="-ml-1" />
          <EnvironmentSelector />
        </div>

        {/* MCP Server Status Indicator */}
        {primaryServerStatus && statusMeta && (
          <div className="flex items-center gap-2 no-drag">
            <button
              onClick={handleStatusClick}
              className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-muted/50 border border-border cursor-pointer hover:bg-muted/70 transition-colors"
              title="Go to MCP Server settings"
            >
              <div
                className="h-2 w-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: statusMeta.indicatorColor }}
              />
              <statusMeta.Icon className={statusMeta.iconClassName} />
              <span className="text-xs font-medium text-muted-foreground">
                {statusMeta.label}
              </span>
            </button>

            {/* Connect button - only show when not connected */}
            {!isConnected && onReconnect && (
              <Button
                onClick={handleReconnect}
                disabled={isReconnecting}
                size="sm"
              >
                {isReconnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reconnecting...
                  </>
                ) : (
                  "Connect"
                )}
              </Button>
            )}
          </div>
        )}
      </div>
      {activeIpc && activeIpc.render({ dismiss: dismissActiveIpc })}
    </header>
  );
};
